<template>
  <div class="">
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <notifier-growl />
    <div v-if="version !== 'dashboard'" class="row m-b-15">
      <SelectCategories
        :ref-categories="filter.categories"
        :ref-store="{
          store: $store,
          mutator: 'SET_CATS_SELECTED',
        }"
      />
      <div class="col-md-9">
        <InputSearch
          ref="inputBar"
          :ref-search="filter.search"
          :ref-store="{
            store: $store,
            mutator: {
              term: 'SET_SEARCH_TERM',
              searching: 'SEARCHING',
            },
          }"
        />
      </div>
    </div>
    <div :class="{ row: version !== 'dashboard' }">
      <div v-if="version !== 'dashboard'" class="col-md-3">
        <LeftSelectorBar
          :ref-selectors="[
            {
              refObj: relevance,
              title: $t('Relevance'),
              type: 'radio',
              name: 'relevance',
              approved: true,
              displayToCompany: false,
              mutator: 'SET_RELEVANCE',
              display: !account.isGuest,
            },
            {
              refObj: $store.getters.singleMarketRegions,
              title: $t('Regions'),
              type: 'checkbox',
              name: 'regions',
              display: !displayMarkets,
              approved: false,
              mutator: 'SET_REGIONS_SELECTED',
            },
            {
              refObj: $store.getters.markets,
              title: $t('Markets'),
              children: $store.getters.marketRegions,
              type: 'checkbox',
              name: 'markets',
              approved: true,
              display: displayMarkets,
              mutator: 'MARKET_SELECTED',
            },
            {
              refObj: jobTypes,
              title: $t('Job type'),
              type: 'radio',
              name: 'ratetype',
              approved: false,
              mutator: 'SET_TYPE_SELECT',
            },
          ]"
        />
      </div>

      <div :class="{ 'col-md-9': version !== 'dashboard' }">
        <ListJobs
          :jobs="$store.getters.jobList"
          :recommended-jobs="$store.getters.recommendList"
          :ref-filter="filter"
          :ref-store="{
            store: $store,
            mutater: 'SET_SORT_BY',
          }"
          :format="version === 'dashboard' ? 'compact' : 'default'"
          :version="version"
          :class="{ 'compact-list-jobs': version === 'dashboard' }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import hashSum from 'hash-sum'
import { FILTER_ACTIONS, JOB_ACTIONS } from '@/state/action-types'
import enums from '@/utils/enum'
import { QueryEncoder } from '@/utils/Encoders'
import ListJobs from '@/components/ListJobs.vue'
import InputSearch from '@/components/InputSearch.vue'
import LeftSelectorBar from '@/components/LeftSelectorBar.vue'
import SelectCategories from '@/components/SelectCategories.vue'

export default {
  name: 'App',
  components: {
    ListJobs,
    InputSearch,
    LeftSelectorBar,
    SelectCategories,
  },
  props: {
    version: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      oldPath: '', // Used for detection of when to send page views to google analytics
      oldQueryHash: '',
      historyNavigation: false,
      enableTracking: false,
    }
  },

  computed: {
    /**
     * This method makes it easy for us to watch the vuex data for state changes
     */
    filter() {
      return this.$store.getters.filter
    },

    relevance() {
      return this.$store.getters.relevance
    },

    jobTypes() {
      return this.$store.getters.jobType
    },

    getTerms() {
      if (this.filter.search.term) {
        return this.filter.search.term.split(',')
      }
      return []
    },

    displayMarkets() {
      return typeof this.account === 'object'
    },
  },

  watch: {
    /**
     * This method watches for updates to our search parameter relevance,
     * and gets new job data in case of changes
     */
    filter: {
      handler() {
        this.updateSearchJobs()
        this.updateQueryParamsFromFilter()
      },
    },
  },

  created() {
    this.oldPath = this.$route.fullPath
    if (this.account.markets) {
      this.account.markets = this.account.markets.map((m) => m.market)
    }
    this.getData()
    this.updateFilterParamsFromQuery()
    this.updateSearchJobs()
    const app = this
    window.onpopstate = () => {
      app.historyNavigation = true
      app.updateFilterParamsFromQuery()
    }
  },

  methods: {
    // Gets filter data such as categories and regions
    async getData() {
      try {
        if (this.version !== 'dashboard') {
          await this.$store.dispatch(FILTER_ACTIONS.INITIATE_JOBS_FILTER)
        }
        if (this.user !== '') {
          await this.$store.dispatch(JOB_ACTIONS.INITIATE_JOBS)
        }
      } catch (e) {
        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.$t('An error occurred when getting filter information'),
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
      }
    },

    /**
     * We send a request for a new list of jobs from an updated search parameter
     */
    async updateSearchJobs() {
      try {
        await this.$store.dispatch(JOB_ACTIONS.SEARCH_JOBS, this.filter)
      } catch (e) {
        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.$t('An error occurred when querying your search'),
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
      }
    },

    /**
     * Method that updates selected filter parts from current query in the browser
     */
    updateFilterParamsFromQuery() {
      // We get the query and the filter
      const query = QueryEncoder.decodeQueryIntoObject(window.location.search)
      this.$store.dispatch(FILTER_ACTIONS.UPDATE_FILTER_FROM_QUERY, query)
    },

    /**
     * Method that updates the current query parameters from our filter
     */
    async updateQueryParamsFromFilter() {
      const q = {} // We create a new query

      // Vuex data sets the query in the users browser
      const { filter } = this
      q.s = filter.search.term
      q.rate_type = filter.type.selected
      q.area = filter.regions.selected ? filter.regions.selected.join() : ''
      q.sort = filter.sortBy
      q.relevance = filter.relevance.selected

      // We update the query from our market
      q.markets = filter.markets.selected.join(',')

      // eslint-disable-next-line camelcase -- Baseline eslint ignore please fix if working on this
      const cat_ids = []
      if (filter.categories.selected) {
        for (const cat in filter.categories.selected) {
          if (filter.categories.selected[cat]) {
            // eslint-disable-next-line camelcase -- Baseline eslint ignore please fix if working on this
            cat_ids.push(filter.categories.selected[cat])
          }
        }
      }

      // eslint-disable-next-line camelcase -- Baseline eslint ignore please fix if working on this
      q.cat_id = cat_ids.join()
      // Update url and send a pageview
      if (!this.historyNavigation && this.oldQueryHash !== hashSum(q)) {
        this.oldQueryHash = hashSum(q)
        await this.$router.push({ path: window.location.pathname, query: q })
      }
      this.historyNavigation = false
      if (typeof window.gtag === 'function' && this.oldPath !== this.$router.fullPath) {
        window.gtag('event', 'page_view', {
          pageview: window.location.path,
        }) // We send a new pageview to google analytics
        this.oldPath = this.$router.fullPath
      }
    },
  },
}
</script>

<style scoped lang="scss"></style>
